import { useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

export function useGenioLuca(profile, initialMsgs = []) {
  const [chatMsgs, setChatMsgs] = useState(initialMsgs);
  const [chatLoading, setChatLoading] = useState(false);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || chatLoading) return;

    const userMsg = { role: 'user', content: text };
    setChatMsgs(prev => [...prev, { autor: 'Tú', texto: text, color: '#5dc1b9', role: 'user' }]);
    setChatLoading(true);

    // Build API messages from history
    const apiMessages = [...chatMsgs
      .filter(m => m.role)
      .map(m => ({ role: m.role, content: m.texto || m.content })),
      userMsg
    ];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setChatLoading(false); return; }

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          messages: apiMessages,
          userId: profile?.id || null,
          userContext: {
            userName: profile?.full_name || 'Emprendedor',
            businessName: profile?.business_name || '',
            businessType: profile?.business_type || '',
            businessStage: profile?.business_stage || '',
            membership: profile?.membership || 'free_observer',
          },
        }),
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // Parse response into Genio and Luca messages
      const responseText = data.text || '';
      const parsed = parseGenioLuca(responseText);
      setChatMsgs(prev => [...prev, ...parsed]);
    } catch (err) {
      console.error('[GenioLuca] Error:', err);
      setChatMsgs(prev => [...prev, {
        autor: 'Sistema',
        texto: 'Hubo un error. Intenta de nuevo.',
        color: '#999',
        role: 'assistant',
      }]);
    } finally {
      setChatLoading(false);
    }
  }, [chatMsgs, chatLoading, profile]);

  return { chatMsgs, setChatMsgs, chatLoading, sendMessage };
}

// Parse "**Genio:** text\n**Luca:** text" into separate messages
function parseGenioLuca(text) {
  const messages = [];
  // Split by **Genio:** or **Luca:** patterns
  const parts = text.split(/\*\*(Genio|Luca)[^*]*\*\*[:\s]*/i).filter(Boolean);

  let currentAutor = null;
  for (const part of parts) {
    const trimmed = part.trim();
    if (/^genio$/i.test(trimmed)) {
      currentAutor = 'Genio ⚡';
    } else if (/^luca$/i.test(trimmed)) {
      currentAutor = 'Luca ◆';
    } else if (trimmed && currentAutor) {
      messages.push({
        autor: currentAutor,
        texto: trimmed,
        color: currentAutor.includes('Genio') ? '#FDBE02' : '#5dc1b9',
        role: 'assistant',
      });
    }
  }

  // Fallback: if parsing failed, show as single message
  if (messages.length === 0 && text.trim()) {
    messages.push({
      autor: 'Genio & Luca',
      texto: text.trim(),
      color: '#FDBE02',
      role: 'assistant',
    });
  }

  return messages;
}
